import { useEffect, useRef, useState, useCallback } from 'react'
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser'
import type { Result } from '@zxing/library'

// ── Types ──────────────────────────────────────────────────────────────────────

/** Represents a camera/video input device available for QR scanning. */
export interface VideoDevice {
  deviceId: string
  label: string
}

/** Lifecycle state of the QR scanner hook. */
export type ScannerState = 'idle' | 'scanning' | 'error'

interface UseQrScannerOptions {
  /** Fires for every successfully decoded QR, debounced (same value within 2.5 s is ignored). */
  onDecode: (text: string) => void
  /** Fires on unrecoverable camera/permission error. */
  onError?: (err: string) => void
}

interface UseQrScannerReturn {
  devices: VideoDevice[]
  selectedDeviceId: string | null
  setSelectedDeviceId: (id: string) => void
  scannerState: ScannerState
  errorMessage: string | null
  /** Attach to the <video> element ref and call this after mounting the video. */
  startScanning: (videoEl: HTMLVideoElement) => void
  stopScanning: () => void
}

// ── localStorage key for persisting the last-chosen camera ────────────────────

const STORAGE_KEY = 'proaxiv.checkin.cameraDeviceId'

/** How long Android's Camera2 HAL typically needs after `track.stop()` before another
 *  `getUserMedia` can succeed. Opening a second camera too soon yields Chrome's
 *  `NotReadableError`: "Could not start video source". */
const ANDROID_CAMERA_RELEASE_MS = 400
/** Backoff between start attempts when the HAL is still busy. */
const CAMERA_START_RETRY_MS = 300
const CAMERA_START_MAX_ATTEMPTS = 3

function isAndroidCamera(): boolean {
  /* v8 ignore next */
  return typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent ?? '')
}

async function delay(ms: number): Promise<void> {
  await new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * Stops every track on `stream`. On Android, also waits for the HAL to drop the
 * exclusive session — JS `readyState === 'ended'` is not enough (TC-SCAN-007).
 */
async function releaseCameraStream(
  stream: { getTracks: () => Array<{ stop: () => void }> } | null
): Promise<void> {
  if (!stream) return
  stream.getTracks().forEach((track) => {
    try {
      track.stop()
    } catch /* v8 ignore next */ {
      // Some mocked / already-stopped tracks throw; ignore.
    }
  })
  /* v8 ignore next 3 */
  if (isAndroidCamera()) {
    await delay(ANDROID_CAMERA_RELEASE_MS)
  }
}

/**
 * `MediaDeviceInfo` doesn't expose facing direction directly, but browsers label devices
 * with hints like "camera 2, facing back" — use those to prefer the rear/environment
 * camera by default. Staff scanning attendee QR codes should see the back camera (which
 * faces the attendee) on first load, not the front/selfie camera (see #159).
 *
 * CAVEAT: this label is generated by the OS/browser and is localized to the device's
 * system language — on a Japanese-set device the label reads e.g. "背面カメラ" instead of
 * "back"/"rear"/"environment", so this heuristic silently fails to match anything (see
 * #238). It's kept only as a last-resort fallback for browsers where the structured
 * `facingMode` capability (used by `detectEnvironmentFacingDeviceId` below) isn't available.
 */
function isBackFacingLabel(label: string): boolean {
  const l = label.toLowerCase()
  if (l.includes('front') || l.includes('user') || l.includes('selfie')) return false
  return l.includes('back') || l.includes('rear') || l.includes('environment')
}

/**
 * Identifies the environment/rear-facing camera's `deviceId` using the structured
 * `facingMode` constraint/capability instead of the (localized, unreliable — see #238)
 * device label. Requests a stream constrained to `facingMode: 'environment'`; the browser
 * picks whichever physical camera satisfies that, and the resolved track's `deviceId`
 * setting tells us which enumerated device that was. Returns `null` if the browser can't
 * satisfy the constraint (e.g. no rear camera, or `facingMode` unsupported).
 */
async function detectEnvironmentFacingDeviceId(): Promise<string | null> {
  let stream: MediaStream | null = null
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { exact: 'environment' } },
    })
    const track = stream.getVideoTracks()[0]
    return track?.getSettings().deviceId ?? null
  } catch {
    return null
  } finally {
    await releaseCameraStream(stream)
  }
}

/**
 * Continuous video scanning fires decode failures on almost every frame with no QR.
 * Those must never reach the UI — only camera/permission/startup failures should.
 */
export function isIgnorableQrScanError(err: Error): boolean {
  const name = err.name ?? ''
  if (
    name === 'NotFoundException' ||
    name === 'ChecksumException' ||
    name === 'FormatException' ||
    name === 'ReaderException'
  ) {
    return true
  }
  const msg = (err.message ?? '').toLowerCase()
  return (
    msg.includes('no multiformat readers were able to detect') ||
    msg.includes('not found') ||
    msg.includes('checksum exception') ||
    msg.includes('format exception')
  )
}

/**
 * Camera hardware still busy after a previous stream was stopped — retryable on
 * Android tablets. Permission / missing-device failures are not.
 */
export function isRetryableCameraError(err: unknown): boolean {
  if (!(err instanceof Error)) {
    return String(err).toLowerCase().includes('could not start video source')
  }
  const name = err.name ?? ''
  if (name === 'NotReadableError' || name === 'AbortError') return true
  if (name === 'NotAllowedError' || name === 'NotFoundError' || name === 'OverconstrainedError') {
    return false
  }
  const msg = (err.message ?? '').toLowerCase()
  return (
    msg.includes('could not start video source') ||
    msg.includes('failed to allocate videosource') ||
    msg.includes('device in use') ||
    msg.includes('starting videoinput failed')
  )
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Manages camera enumeration, device selection, QR scanning, and lifecycle
 * for the ticket verification kiosk.
 *
 * Uses `@zxing/browser` `BrowserMultiFormatReader` which supports all major
 * barcode formats including QR codes. Works on Chrome, Firefox, Safari, Edge
 * over HTTPS or localhost.
 *
 * Camera selection is NOT managed by the browser automatically — we enumerate
 * `MediaDeviceInfo` video inputs and render our own dropdown so staff can
 * choose a USB-connected professional scanner over the built-in laptop webcam.
 */
export function useQrScanner({ onDecode, onError }: UseQrScannerOptions): UseQrScannerReturn {
  const [devices, setDevices] = useState<VideoDevice[]>([])
  const [selectedDeviceId, setSelectedDeviceIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) ?? null
    } catch /* v8 ignore next */ {
      return null
    }
  })
  const [scannerState, setScannerState] = useState<ScannerState>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const readerRef = useRef<BrowserMultiFormatReader | null>(null)
  const controlsRef = useRef<IScannerControls | null>(null)
  // Incremented by every startScanning / stopScanning / unmount. In-flight
  // `decodeFromVideoDevice` promises compare against this so a late-arriving
  // stream is stopped instead of leaking (and so a new start isn't clobbered by
  // an older attempt). Replaces a boolean stop-flag that startScanning used to
  // reset, which could resurrect a session the caller had already stopped.
  const sessionRef = useRef(0)
  const lastDecodedRef = useRef<{ text: string; at: number }>({ text: '', at: 0 })
  const onDecodeRef = useRef(onDecode)
  const onErrorRef = useRef(onError)

  // Keep refs up-to-date without re-registering effects
  useEffect(() => {
    onDecodeRef.current = onDecode
  }, [onDecode])
  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  // ── Enumerate devices on mount ─────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false

    async function enumerate() {
      try {
        // Requesting camera permission first ensures labels are populated
        /* v8 ignore next */
        const stream = await navigator.mediaDevices.getUserMedia({ video: true }).catch(() => null)
        const infos = await navigator.mediaDevices.enumerateDevices()
        await releaseCameraStream(stream)

        /* v8 ignore next */
        if (cancelled) return

        const videoInputs: VideoDevice[] = infos
          .filter((d) => d.kind === 'videoinput')
          .map((d, i) => ({
            deviceId: d.deviceId,
            /* v8 ignore next */
            label: d.label || `Camera ${i + 1}`,
          }))

        // Do NOT publish `devices` yet. TicketVerificationPage starts ZXing as soon as
        // the list is non-empty; publishing here used to overlap with the environment
        // probe's getUserMedia and produced "Could not start video source" on Android
        // tablets (TC-SCAN-007). Finish every probe, then publish list + selection
        // together.
        let storedId: string | null = null
        try {
          storedId = localStorage.getItem(STORAGE_KEY)
        } catch /* v8 ignore next */ {
          storedId = null
        }
        const storedIsValid = !!storedId && videoInputs.some((d) => d.deviceId === storedId)

        // Skip the environment probe when a stored preference is still valid — otherwise
        // a saved front camera plus this probe is front→back→front in a few hundred ms,
        // which is the tablet failure mode.
        const envDeviceId =
          !storedIsValid && videoInputs.length > 1 ? await detectEnvironmentFacingDeviceId() : null
        /* v8 ignore next */
        if (cancelled) return

        setDevices(videoInputs)

        // Auto-select: prefer stored preference, then the environment-facing camera, then a
        // device whose label indicates back/rear-facing, then fall back to the first device.
        setSelectedDeviceIdState((prev) => {
          /* v8 ignore next */
          if (prev && videoInputs.some((d) => d.deviceId === prev)) return prev
          if (envDeviceId && videoInputs.some((d) => d.deviceId === envDeviceId)) return envDeviceId
          const backCamera = videoInputs.find((d) => isBackFacingLabel(d.label))
          /* v8 ignore next */
          return backCamera?.deviceId ?? videoInputs[0]?.deviceId ?? null
        })
      } /* v8 ignore start */ catch (err) {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : 'Camera enumeration failed'
        setErrorMessage(msg)
        onErrorRef.current?.(msg)
      } /* v8 ignore stop */
    }

    enumerate()
    return () => {
      cancelled = true
    }
  }, [])

  // ── Start / stop scanning ──────────────────────────────────────────────────

  const startScanning = useCallback(
    (videoEl: HTMLVideoElement) => {
      const session = ++sessionRef.current
      const previous = controlsRef.current
      controlsRef.current = null
      try {
        previous?.stop()
      } catch /* v8 ignore next */ {
        // ZXing stop() can throw if the stream is already gone.
      }

      if (!readerRef.current) {
        readerRef.current = new BrowserMultiFormatReader()
      }

      setScannerState('scanning')
      setErrorMessage(null)

      const deviceId = selectedDeviceId ?? undefined
      const reader = readerRef.current

      void (async () => {
        /* v8 ignore next 3 */
        if (previous && isAndroidCamera()) {
          await delay(ANDROID_CAMERA_RELEASE_MS)
        }
        for (let attempt = 0; attempt < CAMERA_START_MAX_ATTEMPTS; attempt++) {
          if (session !== sessionRef.current) return
          try {
            const controls = await reader.decodeFromVideoDevice(
              deviceId,
              videoEl,
              (result: Result | undefined, err?: Error) => {
                if (session !== sessionRef.current) return
                /* v8 ignore start */
                if (result) {
                  const text = result.getText()
                  const now = Date.now()
                  if (
                    text === lastDecodedRef.current.text &&
                    now - lastDecodedRef.current.at < 2_500
                  ) {
                    return
                  }
                  lastDecodedRef.current = { text, at: now }
                  onDecodeRef.current(text)
                } else if (err && !isIgnorableQrScanError(err)) {
                  const msg = err.message ?? 'Camera error'
                  setErrorMessage(msg)
                  setScannerState('error')
                  onErrorRef.current?.(msg)
                }
                /* v8 ignore stop */
              }
            )
            if (session !== sessionRef.current) {
              controls.stop()
              return
            }
            controlsRef.current = controls
            return
          } catch (err) {
            if (session !== sessionRef.current) return
            if (isRetryableCameraError(err) && attempt < CAMERA_START_MAX_ATTEMPTS - 1) {
              await delay(CAMERA_START_RETRY_MS)
              continue
            }
            const msg = err instanceof Error ? err.message : 'Failed to start camera'
            setErrorMessage(msg)
            setScannerState('error')
            onErrorRef.current?.(msg)
            return
          }
        }
      })()
    },
    [selectedDeviceId]
  )

  const stopScanning = useCallback(() => {
    sessionRef.current += 1
    try {
      controlsRef.current?.stop()
    } catch /* v8 ignore next */ {
      // ignore
    }
    controlsRef.current = null
    setScannerState('idle')
  }, [])

  // ── Persist selected device id ─────────────────────────────────────────────

  /* v8 ignore start */
  const setSelectedDeviceId = useCallback((id: string) => {
    setSelectedDeviceIdState(id)
    try {
      localStorage.setItem(STORAGE_KEY, id)
    } catch {
      // ignore storage errors
    }
  }, [])
  /* v8 ignore stop */

  // ── Cleanup on unmount ─────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      // Same race as stopScanning(), but avoids calling setState during unmount.
      /* v8 ignore next */
      sessionRef.current += 1
      /* v8 ignore next */
      try {
        controlsRef.current?.stop()
      } catch {
        // ignore
      }
      /* v8 ignore next */
      controlsRef.current = null
    }
  }, [])

  return {
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    scannerState,
    errorMessage,
    startScanning,
    stopScanning,
  }
}
